import {
  type FieldValue,
  formatFieldValue,
  parseFieldInput,
  toInputString,
} from '@fundline/shared/fields/values';
import { type FormEvent, useId, useState } from 'react';
import { Button } from '../../components/ui/Button.tsx';
import { CONTROL } from '../../components/ui/Field.tsx';
import type { FieldDefinition } from '../settings/use-fields.ts';
import type { MerchantFieldValue } from './use-merchants.ts';

const SOURCE_LABELS: Record<string, string> = {
  rep: '',
  ai: 'AI',
  import: 'Import',
  api: 'API',
  system: 'System',
};

/** One field on the merchant profile: shows the value and who set it; edits in place. */
export function MerchantFieldRow({
  field,
  value,
  people,
  onSave,
}: {
  field: FieldDefinition;
  value: MerchantFieldValue | undefined;
  people: ReadonlyMap<string, string>;
  onSave: (value: FieldValue | null) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const id = useId();

  const shown = value && value.value !== null ? formatFieldValue(field, value.value) : '';
  const by =
    value && value.value !== null
      ? value.source === 'rep'
        ? (value.setByUserId && people.get(value.setByUserId)) || 'a former member'
        : (SOURCE_LABELS[value.source] ?? value.source)
      : null;

  function start() {
    setInput(toInputString(field, value?.value ?? null));
    setError(null);
    setEditing(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = parseFieldInput(field, input);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setBusy(true);
    try {
      await onSave(parsed.value);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save.");
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <div className="flex items-start justify-between gap-3 py-3" data-testid="field-value-row">
        <div className="min-w-0">
          <dt className="footnote font-medium text-ink-muted">{field.label}</dt>
          <dd className="mt-0.5 break-words callout text-ink">
            {shown || <span className="text-ink-muted">Not set</span>}
            {value?.source === 'ai' && value.value !== null ? (
              <span
                className="ml-2 rounded-sm bg-brand-tint px-1.5 py-0.5 footnote font-medium text-brand-text"
                title="Filled in by the AI from the conversation"
              >
                AI
              </span>
            ) : null}
          </dd>
          {by && value ? (
            <p className="mt-0.5 footnote text-ink-muted">
              {by === 'AI' ? 'From the conversation' : `By ${by}`} ·{' '}
              {new Date(value.updatedAt).toLocaleDateString()}
            </p>
          ) : null}
        </div>
        <Button variant="ghost" aria-label={`Edit ${field.label}`} onClick={start}>
          Edit
        </Button>
      </div>
    );
  }

  const describedBy = error ? `${id}-error` : undefined;
  const options =
    field.type === 'dropdown'
      ? [
          ...field.options,
          // Keep a stored value that is no longer one of the options visible.
          ...(input && !field.options.includes(input) ? [input] : []),
        ]
      : [];

  return (
    <form
      className="flex flex-col gap-2 py-3"
      onSubmit={(e) => void submit(e)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setEditing(false);
      }}
      noValidate
    >
      <label htmlFor={id} className="footnote font-medium text-ink-muted">
        {field.label}
      </label>
      {field.type === 'dropdown' || field.type === 'boolean' ? (
        <select
          id={id}
          className={CONTROL}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          autoFocus
        >
          <option value="">Not set</option>
          {field.type === 'boolean' ? (
            <>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </>
          ) : (
            options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))
          )}
        </select>
      ) : (
        <div className="flex items-center gap-2">
          {field.type === 'currency' ? <span className="text-ink-muted">$</span> : null}
          <input
            id={id}
            className={CONTROL}
            type={field.type === 'date' ? 'date' : field.systemKey === 'email' ? 'email' : 'text'}
            inputMode={field.type === 'number' || field.type === 'currency' ? 'decimal' : undefined}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            autoFocus
          />
        </div>
      )}
      {error ? (
        <p id={`${id}-error`} className="footnote text-danger">
          {error}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" busy={busy}>
          Save
        </Button>
        <Button variant="ghost" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
